import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { works, genreOptions } from "@/mocks/works";
import Breadcrumb from "@/components/base/Breadcrumb";
import Pagination from "@/components/base/Pagination";
import WorkCard from "@/components/feature/WorkCard";
import FilterBar from "@/pages/works/components/FilterBar";

const PAGE_SIZE = 12;

const TYPE_TABS = [
  { key: "all", label: "全部作品" },
  { key: "movie", label: "电影" },
  { key: "tv", label: "电视剧" },
];

export default function WorksPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const query = searchParams.get("q") ?? "";
  const type = searchParams.get("type") ?? "all";
  const year = searchParams.get("year") ?? "all";
  const genre = searchParams.get("genre") ?? "all";
  const sort = searchParams.get("sort") ?? "latest";
  const page = Math.max(1, Number(searchParams.get("page") ?? "1") || 1);

  const years = useMemo(
    () => Array.from(new Set(works.map((work) => work.year))).sort((a, b) => b - a),
    [],
  );

  const update = (patch: Record<string, string>, keepPage = false) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (!value || value === "all") {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    });
    if (!keepPage) next.delete("page");
    setSearchParams(next, { replace: true });
  };

  const filtered = useMemo(() => {
    const keyword = query.trim();
    const list = works.filter((work) => {
      if (type !== "all" && work.type !== type) return false;
      if (year !== "all" && String(work.year) !== year) return false;
      if (genre !== "all" && !work.genres.includes(genre)) return false;
      if (keyword) {
        const hit =
          work.title.includes(keyword) ||
          work.originalTitle.includes(keyword) ||
          work.genres.some((g) => g.includes(keyword)) ||
          work.cast.some((c) => c.includes(keyword));
        if (!hit) return false;
      }
      return true;
    });

    return list.sort((a, b) => {
      if (sort === "views") return b.views - a.views;
      return b.year - a.year || b.id - a.id;
    });
  }, [query, type, year, genre, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const hasFilter = Boolean(query) || type !== "all" || year !== "all" || genre !== "all" || sort !== "latest";

  const resetFilters = () => setSearchParams(new URLSearchParams(), { replace: true });

  const currentTabLabel =
    type === "movie" ? "电影" : type === "tv" ? "电视剧" : "电影 / 电视剧";

  return (
    <div className="pt-24 md:pt-28">
      <div className="bg-background-100 border-b border-background-200">
        <div className="max-w-7xl mx-auto w-full px-4 md:px-8 py-8 md:py-12">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "作品分类" }]} />
          <h1 className="mt-5 font-heading text-2xl md:text-4xl font-semibold text-foreground-950">
            叶山小百合全部影视作品
          </h1>
          <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
            收录叶山小百合参演的 {currentTabLabel} 作品，可按上映年份、作品类型筛选，并按最新上线或点击量排序，帮助你快速定位想看的作品。
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto w-full px-4 md:px-8 py-10 md:py-14">
        <div className="flex items-center gap-2 mb-6 overflow-x-auto no-scrollbar">
          {TYPE_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => update({ type: tab.key })}
              className={[
                "h-10 px-5 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer",
                type === tab.key
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 border border-background-200 text-foreground-700 hover:border-primary-300",
              ].join(" ")}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="mb-8 flex flex-col gap-3">
          <FilterBar
            years={years}
            genres={genreOptions}
            year={year}
            genre={genre}
            sort={sort}
            onChange={(patch) => update(patch)}
          />
          {query && (
            <p className="text-sm text-foreground-600">
              搜索关键词：
              <span className="text-primary-600 font-medium mx-1">{query}</span>
              共找到 <span className="text-primary-600 font-medium">{filtered.length}</span> 部作品
            </p>
          )}
        </div>

        {paged.length > 0 ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {paged.map((work) => (
                <WorkCard key={work.id} work={work} />
              ))}
            </div>

            <div className="mt-12">
              <Pagination
                current={currentPage}
                total={totalPages}
                onChange={(nextPage) => update({ page: String(nextPage) }, true)}
              />
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <span className="w-16 h-16 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center mb-5">
              <i className="ri-file-search-line text-3xl"></i>
            </span>
            <h3 className="font-heading text-lg font-semibold text-foreground-900">
              暂无符合条件的作品
            </h3>
            <p className="mt-2 text-sm text-foreground-500 max-w-md">
              试试调整筛选条件或关键词，或重置筛选查看全部作品列表。
            </p>
            {hasFilter && (
              <button
                type="button"
                onClick={resetFilters}
                className="mt-6 inline-flex items-center gap-2 px-5 h-11 rounded-md bg-accent-500 text-accent-950 text-sm font-medium hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-refresh-line"></i>
                </span>
                重置筛选
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}